/**
 * Roles defined for the xylemdatalake.com → xylemsprout.com migration (XDS-3387).
 * Mirrors the legacy section grouping in xylem-datalake/Xylem_Data_Lake_Migration_Plan.md.
 *
 * Used by AuthGuard to gate migrated routes via route data.permission. The
 * roles themselves are created in XGS manually (role-list → "Add Role"); see
 * docs/roles/*.md for the canonical name + description for each.
 */

export const LEGACY_DATALAKE_PERMISSION_PREFIX = 'datalake:';

export const LegacyDatalakeRole = {
  DataScience: 'XylemDatalakeDataScience',
  RMA: 'XylemDatalakeRMA',
  MeterHistory: 'XylemDatalakeMeterHistory',
  Backspool: 'XylemDatalakeBackspool',
  Reports: 'XylemDatalakeReports',
  QuickSight: 'XylemDatalakeQuickSight',
  Admin: 'XylemDatalakeAdmin'
} as const;

export type LegacyDatalakeRoleName =
  (typeof LegacyDatalakeRole)[keyof typeof LegacyDatalakeRole];

/** Roles that bypass datalake permission checks regardless of route. */
export const LEGACY_DATALAKE_SUPER_ROLES: ReadonlyArray<string> = [
  'OXI:APPLICATIONADMIN',
  LegacyDatalakeRole.DataScience
];

/**
 * Permission key (set on each route's data.permission) → roles that grant access.
 * AuthGuard reads this map to decide whether a user can activate a migrated route.
 *
 * Keys are prefixed with `datalake:` so the guard can detect a datalake permission
 * without a hard-coded list of route paths.
 */
export const LEGACY_DATALAKE_ROUTE_ACCESS: Readonly<
  Record<string, ReadonlyArray<LegacyDatalakeRoleName>>
> = {
  [`${LEGACY_DATALAKE_PERMISSION_PREFIX}meter-history`]: [
    LegacyDatalakeRole.MeterHistory
  ],
  [`${LEGACY_DATALAKE_PERMISSION_PREFIX}backspool`]: [
    LegacyDatalakeRole.Backspool
  ],
  [`${LEGACY_DATALAKE_PERMISSION_PREFIX}quicksight`]: [
    LegacyDatalakeRole.QuickSight
  ],
  [`${LEGACY_DATALAKE_PERMISSION_PREFIX}reports`]: [LegacyDatalakeRole.Reports],
  [`${LEGACY_DATALAKE_PERMISSION_PREFIX}rma`]: [LegacyDatalakeRole.RMA],
  [`${LEGACY_DATALAKE_PERMISSION_PREFIX}admin`]: [LegacyDatalakeRole.Admin]
};

export function isLegacyDatalakePermission(permission: unknown): boolean {
  return (
    typeof permission === 'string' &&
    permission.startsWith(LEGACY_DATALAKE_PERMISSION_PREFIX)
  );
}

/**
 * True when any of the user's role names grants access to the given datalake
 * permission key. Super roles bypass the per-route check.
 */
export function userHasLegacyDatalakeAccess(
  userRoleNames: ReadonlyArray<string>,
  permissionKey: string
): boolean {
  if (userRoleNames.some(name => LEGACY_DATALAKE_SUPER_ROLES.includes(name))) {
    return true;
  }
  const allowed = LEGACY_DATALAKE_ROUTE_ACCESS[permissionKey];
  if (!allowed) {
    return false;
  }
  return userRoleNames.some(name =>
    (allowed as ReadonlyArray<string>).includes(name)
  );
}
